import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────────────────────── date helpers ──────────────────────────
const iso = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => {
  if (!s) return null;
  const [y, m, dd] = s.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, dd));
};
const addDays = (d, n) => {
  const nd = new Date(d);
  nd.setUTCDate(nd.getUTCDate() + n);
  return nd;
};
const sameDay = (a, b) => a && b && iso(a) === iso(b);
const monthLabel = (d) =>
  d.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });

// Return list of dates covering the month grid (Mon-first, 6 rows)
function monthGrid(anchor) {
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  // 0=Sun, 1=Mon...; UK grid starts Monday
  const dow = (first.getUTCDay() + 6) % 7;
  const start = addDays(first, -dow);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// Group units for the dropdown
function groupUnits(unitTypes) {
  const groups = [];
  for (const t of unitTypes) {
    const gravel = (t.units || []).filter((u) => u.id?.startsWith("gravel-"));
    const grass = (t.units || []).filter((u) => u.id?.startsWith("grass-ehu-"));
    const tent = (t.units || []).filter((u) => u.id?.startsWith("tent-"));
    const lodges = (t.units || []).filter((u) => u.id?.startsWith("lodge-"));
    if (lodges.length) groups.push({ label: "Lodges", type: t.type, units: lodges });
    if (gravel.length) groups.push({ label: "Gravel Pitches (EHU)", type: t.type, units: gravel });
    if (grass.length) groups.push({ label: "Grass Pitches (EHU)", type: t.type, units: grass });
    if (tent.length) groups.push({ label: "Tent Pitches (no EHU)", type: t.type, units: tent });
  }
  return groups;
}

// ────────────────────────── component ──────────────────────────
const BookingCalendar = ({ units }) => {
  const groups = useMemo(() => groupUnits(units), [units]);
  const flatUnits = useMemo(
    () =>
      groups.flatMap((g) =>
        g.units.map((u) => ({ ...u, unit_type: g.type, group: g.label }))
      ),
    [groups]
  );

  const [viewMode, setViewMode] = useState("single"); // "single" | "multi"
  const [unitId, setUnitId] = useState(flatUnits[0]?.id || "");
  const [groupIdx, setGroupIdx] = useState(0);
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1));
  });
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rangeStart, setRangeStart] = useState(null); // {date, unitId} in multi mode
  const [busy, setBusy] = useState(false);

  const nextMonth = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 1));

  const load = async () => {
    setLoading(true);
    try {
      const [b, k] = await Promise.all([
        axios.get(`${API}/admin/blocks`),
        axios.get(`${API}/admin/bookings`),
      ]);
      setBlocks(b.data || []);
      setBookings(
        (k.data || []).filter((x) =>
          ["paid", "pending"].includes(x.payment_status)
        )
      );
    } catch {
      toast.error("Could not load calendar data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setRangeStart(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, viewMode, groupIdx]);

  // Per-unit day-state map: unitId → (isoDate → {state, block, booking})
  const dayIndexByUnit = useMemo(() => {
    const master = new Map();
    const ensure = (uid) => {
      if (!master.has(uid)) master.set(uid, new Map());
      return master.get(uid);
    };
    for (const b of bookings) {
      const start = parseIso(b.start_date);
      const end = parseIso(b.end_date);
      if (!start || !end) continue;
      const m = ensure(b.unit_id);
      for (let d = new Date(start); d < end; d = addDays(d, 1)) {
        m.set(iso(d), {
          state: b.payment_status === "paid" ? "booked" : "pending",
          booking: b,
        });
      }
    }
    for (const blk of blocks) {
      const start = parseIso(blk.start_date);
      const end = parseIso(blk.end_date);
      if (!start || !end) continue;
      const m = ensure(blk.unit_id);
      for (let d = new Date(start); d < end; d = addDays(d, 1)) {
        const k = iso(d);
        if (!m.has(k)) m.set(k, { state: "blocked", block: blk });
      }
    }
    return master;
  }, [blocks, bookings]);

  // Click a date. Works for both single & multi view.
  const onCellClick = async (date, targetUnitId) => {
    if (busy) return;
    const key = iso(date);
    const map = dayIndexByUnit.get(targetUnitId) || new Map();
    const st = map.get(key);
    if (st && (st.state === "booked" || st.state === "pending")) {
      toast.info("This date is booked — can't be changed from here.");
      return;
    }

    // Blocked cell → offer to unblock the containing block
    if (st && st.state === "blocked") {
      const blk = st.block;
      if (!blk) return;
      const ok = window.confirm(
        `Remove block "${blk.reason || "Unavailable"}" (${blk.start_date} → ${blk.end_date}) ?`
      );
      if (!ok) return;
      setBusy(true);
      try {
        await axios.delete(`${API}/admin/blocks/${blk.id}`);
        toast.success("Block removed. Dates opened up.");
        await load();
      } catch {
        toast.error("Could not remove block.");
      } finally {
        setBusy(false);
      }
      return;
    }

    // Free cell — range logic. Range is per-unit: start & end must be same unit.
    if (!rangeStart) {
      setRangeStart({ date, unitId: targetUnitId });
      return;
    }
    if (rangeStart.unitId !== targetUnitId) {
      // Switched unit — start a new range
      setRangeStart({ date, unitId: targetUnitId });
      return;
    }
    if (sameDay(rangeStart.date, date)) {
      setRangeStart(null);
      return;
    }
    let s = rangeStart.date;
    let e = date;
    if (e < s) [s, e] = [e, s];
    const endExclusive = addDays(e, 1);
    // Ensure no overlap with an existing booking
    for (let d = new Date(s); d < endExclusive; d = addDays(d, 1)) {
      const st2 = map.get(iso(d));
      if (st2 && (st2.state === "booked" || st2.state === "pending")) {
        toast.error("Range includes a booked date. Pick a range without existing bookings.");
        setRangeStart(null);
        return;
      }
    }
    const unitDef = flatUnits.find((u) => u.id === targetUnitId);
    setBusy(true);
    try {
      await axios.post(`${API}/admin/blocks`, {
        unit_type: unitDef.unit_type,
        unit_id: unitDef.id,
        start_date: iso(s),
        end_date: iso(endExclusive),
        reason: "Blocked (admin)",
      });
      toast.success(`Blocked ${unitDef.name}: ${iso(s)} → ${iso(endExclusive)}.`);
      setRangeStart(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create block.");
    } finally {
      setBusy(false);
    }
  };

  const renderMonth = (monthAnchor) => {
    const grid = monthGrid(monthAnchor);
    const monthIdx = monthAnchor.getUTCMonth();
    const map = dayIndexByUnit.get(unitId) || new Map();
    return (
      <div
        className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-6"
        data-testid={`calendar-month-${iso(monthAnchor)}`}
      >
        <div className="font-serif text-lg md:text-xl text-[#14100F] mb-4">
          {monthLabel(monthAnchor)}
        </div>
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.16em] text-[#6A5F58] pb-2">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
            <div key={d} className="text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d) => {
            const inMonth = d.getUTCMonth() === monthIdx;
            const key = iso(d);
            const state = map.get(key)?.state;
            const isRangeStart =
              rangeStart && rangeStart.unitId === unitId && sameDay(rangeStart.date, d);
            const today = sameDay(new Date(), d);

            let cls = "";
            if (!inMonth) cls = "opacity-30 bg-[#F5EFE6] text-[#6A5F58]";
            else if (state === "booked")
              cls = "bg-[#0F0E0E] text-[#F5EFE6] cursor-not-allowed";
            else if (state === "pending")
              cls = "bg-[#C89B5F] text-[#0F0E0E] cursor-not-allowed";
            else if (state === "blocked")
              cls = "bg-[#B76E79] text-[#F5EFE6] hover:bg-[#8C4E58] cursor-pointer";
            else cls = "bg-[#F5EFE6] text-[#14100F] hover:bg-[#EFE1CE] cursor-pointer";

            if (isRangeStart) cls += " ring-2 ring-[#B76E79]";
            if (today) cls += " font-bold";

            return (
              <button
                key={key}
                onClick={() => inMonth && onCellClick(d, unitId)}
                disabled={!inMonth || busy}
                data-testid={`cal-cell-${key}`}
                className={`h-11 md:h-12 rounded-md text-sm transition-all ${cls}`}
                title={
                  state === "booked"
                    ? "Booked (paid)"
                    : state === "pending"
                    ? "Pending payment"
                    : state === "blocked"
                    ? "Blocked — click to unblock"
                    : "Free — click to start range"
                }
              >
                {d.getUTCDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Multi-unit strip: rows = units, columns = days of the anchor month
  const renderMultiGrid = () => {
    const currentGroup = groups[groupIdx] || groups[0];
    if (!currentGroup) return null;
    const y = anchor.getUTCFullYear();
    const m = anchor.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const dates = Array.from({ length: daysInMonth }, (_, i) => new Date(Date.UTC(y, m, i + 1)));

    return (
      <div
        className="bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden"
        data-testid="calendar-multi-grid"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#F5EFE6] text-[#6A5F58] uppercase tracking-[0.14em] text-[10px]">
                <th className="text-left px-3 py-2 sticky left-0 bg-[#F5EFE6] min-w-[180px] z-10">
                  {currentGroup.label}
                </th>
                {dates.map((d) => {
                  const dow = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"][(d.getUTCDay() + 6) % 7];
                  const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                  return (
                    <th
                      key={iso(d)}
                      className={`text-center py-2 px-1 min-w-[28px] ${
                        isWeekend ? "text-[#B76E79]" : ""
                      }`}
                    >
                      <div>{dow}</div>
                      <div className="text-[#0F0E0E] font-serif normal-case text-sm mt-0.5">
                        {d.getUTCDate()}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {currentGroup.units.map((u) => {
                const map = dayIndexByUnit.get(u.id) || new Map();
                return (
                  <tr key={u.id} className="border-t border-[#DDD1BE] hover:bg-[#F5EFE6]/40">
                    <td
                      className="px-3 py-1.5 text-[#14100F] font-medium sticky left-0 bg-white z-10 whitespace-nowrap"
                      data-testid={`multi-unit-label-${u.id}`}
                    >
                      {u.name}
                    </td>
                    {dates.map((d) => {
                      const key = iso(d);
                      const state = map.get(key)?.state;
                      const isRangeStart =
                        rangeStart && rangeStart.unitId === u.id && sameDay(rangeStart.date, d);
                      const today = sameDay(new Date(), d);

                      let cls = "";
                      if (state === "booked")
                        cls = "bg-[#0F0E0E] cursor-not-allowed";
                      else if (state === "pending")
                        cls = "bg-[#C89B5F] cursor-not-allowed";
                      else if (state === "blocked")
                        cls = "bg-[#B76E79] hover:bg-[#8C4E58] cursor-pointer";
                      else cls = "bg-[#F5EFE6] hover:bg-[#EFE1CE] cursor-pointer";

                      if (isRangeStart) cls += " ring-2 ring-inset ring-[#B76E79]";
                      if (today) cls += " outline outline-1 outline-[#0F0E0E]/40";

                      return (
                        <td key={key} className="p-0 text-center">
                          <button
                            onClick={() => onCellClick(d, u.id)}
                            disabled={busy}
                            data-testid={`multi-cell-${u.id}-${key}`}
                            title={
                              state === "booked"
                                ? "Booked (paid)"
                                : state === "pending"
                                ? "Pending payment"
                                : state === "blocked"
                                ? "Blocked — click to unblock"
                                : "Free — click to start range"
                            }
                            className={`w-full h-9 md:h-8 transition-colors ${cls}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <section data-testid="booking-calendar">
      <p className="overline text-[#6A5F58]">
        <span className="marker-line" /> Booking calendar
      </p>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">
        Manual block &amp; open
      </h2>
      <p className="mt-3 text-[#6A5F58] text-sm max-w-3xl">
        Click a free date to start a range, then click the end date to block those nights.
        Click a blocked date to open it back up. Booked dates are locked.
      </p>

      {/* Controls */}
      <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-6 flex flex-col md:flex-row items-stretch md:items-center gap-4 justify-between flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/* View mode toggle */}
          <div className="inline-flex rounded-lg overflow-hidden border border-[#DDD1BE]" data-testid="cal-view-toggle">
            <button
              onClick={() => setViewMode("single")}
              className={`px-3 py-2 text-xs font-medium tracking-[0.14em] uppercase transition-colors ${
                viewMode === "single"
                  ? "bg-[#0F0E0E] text-[#F5EFE6]"
                  : "bg-[#F5EFE6] text-[#3A322E] hover:bg-[#EFE1CE]"
              }`}
              data-testid="cal-view-single"
            >
              Single unit
            </button>
            <button
              onClick={() => setViewMode("multi")}
              className={`px-3 py-2 text-xs font-medium tracking-[0.14em] uppercase transition-colors ${
                viewMode === "multi"
                  ? "bg-[#0F0E0E] text-[#F5EFE6]"
                  : "bg-[#F5EFE6] text-[#3A322E] hover:bg-[#EFE1CE]"
              }`}
              data-testid="cal-view-multi"
            >
              All units
            </button>
          </div>

          {viewMode === "single" ? (
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger
                className="w-full md:w-72 bg-[#F5EFE6] h-11"
                data-testid="cal-unit-select"
              >
                <SelectValue placeholder="Choose a unit" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectGroup key={g.label}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select
              value={String(groupIdx)}
              onValueChange={(v) => setGroupIdx(Number(v))}
            >
              <SelectTrigger
                className="w-full md:w-72 bg-[#F5EFE6] h-11"
                data-testid="cal-group-select"
              >
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g, i) => (
                  <SelectItem key={g.label} value={String(i)}>
                    {g.label} ({g.units.length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {loading && <Loader2 size={16} className="animate-spin text-[#B76E79]" />}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              setAnchor(
                new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() - 1, 1))
              )
            }
            className="p-2 rounded-full bg-[#F5EFE6] hover:bg-[#EFE1CE] transition"
            data-testid="cal-prev"
            aria-label="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="min-w-[220px] text-center font-serif text-[#14100F] text-lg">
            {monthLabel(anchor)}
          </div>
          <button
            onClick={() =>
              setAnchor(
                new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 1))
              )
            }
            className="p-2 rounded-full bg-[#F5EFE6] hover:bg-[#EFE1CE] transition"
            data-testid="cal-next"
            aria-label="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-3 flex-wrap text-xs text-[#6A5F58]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#F5EFE6] border border-[#DDD1BE]" /> Free
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#B76E79]" /> Blocked (click to open)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#C89B5F]" /> Pending
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#0F0E0E]" /> Booked (paid)
        </span>
        {rangeStart && (
          <span className="inline-flex items-center gap-2 ml-auto text-[#B76E79] font-medium">
            Range start: {iso(rangeStart.date)}
            {viewMode === "multi" ? ` (${(flatUnits.find((u) => u.id === rangeStart.unitId) || {}).name || rangeStart.unitId})` : ""}
             — click end date to block.
            <button
              onClick={() => setRangeStart(null)}
              className="p-1 rounded-full hover:bg-[#B76E79]/10"
              data-testid="cal-cancel-range"
              aria-label="Cancel range"
            >
              <X size={14} />
            </button>
          </span>
        )}
      </div>

      {viewMode === "single" ? (
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
          {renderMonth(anchor)}
          {renderMonth(nextMonth)}
        </div>
      ) : (
        <div className="mt-6">{renderMultiGrid()}</div>
      )}
    </section>
  );
};

export default BookingCalendar;
